import { useEffect, useRef, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import {
  CalendarPlus, CircleCheck, Play, Plus, RefreshCw, ShieldAlert, Trash2, Type as TypeIcon, Upload,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { get, post, del, ApiError } from '@/lib/api';
import { runBulkPreflightWithRecovery, type StoreLike } from '@/lib/preflight-recovery';
import { formatRelative } from '@/lib/time';
import { Thumbnail } from './Thumbnail';
import { PreflightCellRow } from './PreflightCellRow';
import { STORE_NAME_NUMBER_FONTS, normalizeStoreFont } from '@/lib/store-personalization-font';
import { describeLiveEdit, type LiveArtPending } from '@/lib/live-art';

export type PreflightEntry = {
  status?: 'pass' | 'fail' | string;
  details?: string;
  lastRun?: string;
  runBy?: string;
};

export type TeamStoreLogo = {
  id?: string;
  logoId?: string;
  label?: string;
  url?: string;
  gcsPath?: string;
  type?: string;
  text?: string;
  font?: string;
  compositeUrlByTextColor?: Record<string, string>;
  preflight?: Record<string, PreflightEntry>;
};

export type ShopifyProductRow = {
  shopifyProductId?: string;
  shopifyGid?: string;
  productConfigId?: string;
  title?: string;
  tier?: string;
  designId?: string;
  variantCount?: number;
  archived?: boolean;
};

export type OrderStats = {
  totalOrders?: number;
  totalUnits?: number;
  totalRevenue?: number;
  totalCommission?: number;
  totalSalesRepCommission?: number;
  raised_amount?: number;
  lastOrderAt?: string;
  lastOrderId?: string;
  quarterOrders?: number;
  quarterRevenue?: number;
  quarterUnits?: number;
};

export type TeamStoreDetail = {
  id: string;
  status?: string;
  schoolName?: string;
  organizationName?: string;
  fundraiserName?: string;
  sportType?: string;
  sport_id?: string;
  gender?: string | null;
  sport_gender?: string | null;
  mascot?: string;
  ghostfit_id?: string;
  storeNumberDisplay?: string;
  startDate?: string;
  endDate?: string | null;
  primaryColor?: string;
  primaryColorName?: string;
  secondaryColor?: string;
  secondaryColorName?: string;
  tertiaryColor?: string;
  tertiaryColorName?: string;
  logoPath?: string;
  logos?: TeamStoreLogo[];
  collectionUrl?: string;
  storeUrl?: string;
  store_url?: string;
  store_handle?: string;
  shopifyCollectionUrl?: string;
  shopifyCollectionHandle?: string;
  shopifyCollectionId?: string;
  shopify_sport_collection_gid?: string | null;
  shopifyCollectionGid?: string | null;
  shopify_collection_gid?: string | null;
  productTemplateId?: string;
  artworkTemplateId?: string;
  sportTemplateId?: string;
  useDesignTemplates?: boolean;
  adminEmail?: string;
  salesRepEmail?: string;
  salesRepName?: string;
  commissionRate?: number;
  salesRepCommissionRate?: number;
  lastRolloutAt?: string;
  lastRolloutId?: string;
  lastRolloutPreviewAt?: string;
  lastRolloutResult?: { dryRun?: boolean; created?: number; skipped?: number; wouldCreate?: number; error?: string | null; at?: string };
  leading_edge_event_id?: string;
  leading_edge_external_id?: string;
  leading_edge_callback_url?: string;
  source?: string;
  submittedBy?: string;
  head_coach_name?: string;
  head_coach_email?: string;
  roster_count?: number;
  shopifyProductSummary?: ShopifyProductRow[];
  orderStats?: OrderStats;
  extensions?: { oldDate?: string; newDate?: string; extendedBy?: string; shopifySynced?: boolean }[];
  preflightOverridden?: boolean;
  preflightOverrideReason?: string;
  artworkBySize?: Record<string, { passed?: boolean; overridden?: boolean }>;
  personalization?: { font?: string; enabled?: boolean; hanger?: boolean; mode?: string };
  playerPacks?: { enabled: boolean; required: boolean };
  liveAt?: unknown;
  productsCreatedAt?: unknown;
  /** A logo replace / caption edit saved on a selling store, not yet reprinted. */
  liveArtPending?: LiveArtPending;
};

// Statuses where logo upload/replace/text edits are allowed (mirrors
// lib/teamstore-wizard.js LOGO_EDITABLE_STATUSES on the server).
const EDITABLE = new Set(['pending_review', 'pending_artwork', 'artwork_ready', 'artwork_failed', 'shopify_error']);
const APPROVABLE = new Set(['pending_review', 'artwork_ready', 'artwork_failed']);

const STATUS_TONE: Record<string, string> = {
  pending_review:    'bg-bg text-muted',
  pending_artwork:   'bg-warning/10 text-warning',
  artwork_ready:     'bg-primary/10 text-primary',
  artwork_failed:    'bg-danger/10 text-danger',
  approved:          'bg-warning/10 text-warning',
  creating_products: 'bg-primary/10 text-primary',
  live:              'bg-success/10 text-success',
  closing_soon:      'bg-warning/10 text-warning',
  shopify_error:     'bg-danger/10 text-danger',
  live_with_errors:  'bg-warning/10 text-warning',
};

export function gcsToPath(v?: string): string | null {
  if (!v) return null;
  if (v.startsWith('gs://ghostfit-fundraiser-logos/')) return v.slice('gs://ghostfit-fundraiser-logos/'.length);
  if (v.startsWith('https://storage.googleapis.com/ghostfit-fundraiser-logos/')) {
    return v.slice('https://storage.googleapis.com/ghostfit-fundraiser-logos/'.length);
  }
  if (!v.startsWith('gs://') && !v.startsWith('http')) return v;
  return null;
}

function errMsg(e: unknown, fallback: string): string {
  const body = (e as ApiError).body as { message?: string; error?: string } | null;
  return body?.message || body?.error || fallback;
}

export type FontsPayload = { fonts?: (string | { name?: string })[] } | (string | { name?: string })[];

export function fontNames(data?: FontsPayload): string[] {
  const arr = Array.isArray(data) ? data : data?.fonts;
  if (!Array.isArray(arr)) return [];
  return arr.map((f) => (typeof f === 'string' ? f : f?.name || '')).filter(Boolean);
}

export function StoreNameNumberFontCard({
  store,
  refresh,
}: {
  store: TeamStoreDetail;
  refresh: () => Promise<unknown>;
}) {
  const saved = normalizeStoreFont(store.personalization?.font);
  const [font, setFont] = useState(saved);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFont(saved); }, [saved]);

  async function save() {
    setBusy(true);
    try {
      const r = await post<{ shopifySynced?: boolean; message?: string }>(
        `/api/team-stores/${store.id}/personalization-font`,
        { font },
      );
      if (r.shopifySynced) toast.success(r.message || 'Name/number face saved');
      else toast.warning(r.message || 'Saved on the store; Shopify collection metafield was not written');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save name/number face'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Name / number face</h3>
      <p className="mt-1 text-[12px] text-muted">
        One face for this store. Every Personalize preview location uses it. Shoppers do not pick a font.
      </p>
      <div className="mt-2.5 flex gap-2">
        <select
          aria-label="Name and number face for this store"
          className="h-8 flex-1 rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[12px]"
          value={font}
          onChange={(e) => setFont(normalizeStoreFont(e.target.value))}
        >
          {STORE_NAME_NUMBER_FONTS.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <Button size="sm" disabled={busy || font === saved} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function LogoCard({ store, logo, refresh }: { store: TeamStoreDetail; logo: TeamStoreLogo; refresh: () => Promise<unknown> }) {
  const logoId = (logo.id ?? logo.logoId) as string;
  const editable = EDITABLE.has(store.status || '');
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [textOpen, setTextOpen] = useState(false);
  const [text, setText] = useState(logo.text || '');
  const [font, setFont] = useState(logo.font || 'default');
  const { data: fontsData } = useSWR<FontsPayload>(textOpen ? '/api/fonts' : null, get);
  const fonts = fontNames(fontsData);

  async function replaceFile(file: File) {
    setBusy('replace');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetch(`/api/team-stores/${store.id}/logos/${encodeURIComponent(logoId)}/replace`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, body);
      // A SELLING store: saved, but the old print keeps selling until approved.
      const live = describeLiveEdit(body);
      if (live) toast.message(live.message);
      else toast.success('Logo replaced — composites and preflight cleared');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Replace failed'));
    } finally {
      setBusy(null);
    }
  }

  async function applyText() {
    setBusy('text');
    try {
      const r = await post<Parameters<typeof describeLiveEdit>[0]>(
        `/api/team-stores/${store.id}/logos/${encodeURIComponent(logoId)}/text`, { text, font },
      );
      // A SELLING store: the caption reprints in place; 207 = saved but the
      // reprint did not finish (the server also recorded an errorEvent).
      const live = describeLiveEdit(r);
      if (live?.kind === 'warning') toast.warning(live.message);
      else if (live) toast.success(live.message);
      else toast.success(text ? 'Text applied (white + black composites)' : 'Text cleared');
      setTextOpen(false);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Text apply failed'));
    } finally {
      setBusy(null);
    }
  }

  async function removeLogo() {
    if (!confirm(`Remove ${logo.label || logoId} from this store?`)) return;
    setBusy('delete');
    try {
      await del(`/api/team-stores/${store.id}/logos/${encodeURIComponent(logoId)}`);
      toast.success('Logo removed');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Remove failed'));
    } finally {
      setBusy(null);
    }
  }

  async function runAllCells() {
    setBusy('cells');
    try {
      // Recovery: Safari aborts silent fetches around ~50s and a multi-cell
      // bulk run legitimately takes that long. On a network-level drop the
      // run keeps going server-side and lands in one Firestore transaction —
      // poll the store doc for it instead of dead-ending.
      const r = await runBulkPreflightWithRecovery({
        logoId,
        run: () => post<{ cellCount?: number; passedCount?: number }>(
          `/api/team-stores/${store.id}/preflight-all/${encodeURIComponent(logoId)}`, undefined
        ),
        fetchStore: () => get<StoreLike>(`/api/active-stores/${store.id}`),
        isRealError: (e) => e instanceof ApiError,
        onRecovering: () => setBusy('cells-recovering'),
      });
      toast.success(`${r.passed}/${r.total} cells pass${r.recovered ? ' (recovered after dropped connection)' : 'ed'}`);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, (e as Error)?.message || 'Bulk preflight failed'));
    } finally {
      setBusy(null);
    }
  }

  const preflightEntries = Object.entries(logo.preflight ?? {});
  const composites = Object.entries(logo.compositeUrlByTextColor ?? {});

  return (
    <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
      <div className="flex items-start gap-3">
        {logo.gcsPath ? (
          <Thumbnail path={logo.gcsPath} alt={logo.label ?? 'logo'} className="h-14 w-14 shrink-0" />
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[8px] bg-bg text-[10px] text-subtle">—</div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-medium">{logo.label ?? 'logo'}</p>
          <p className="font-mono text-[10px] text-subtle">{logoId}</p>
          {logo.text && <p className="mt-0.5 truncate text-[11px] text-muted">“{logo.text}” · {logo.font}</p>}
        </div>
        <Button size="sm" variant="secondary" disabled={!!busy} onClick={runAllCells}>
          {busy === 'cells' ? 'Running…' : busy === 'cells-recovering' ? 'Recovering…' : 'Run all cells'}
        </Button>
      </div>

      {composites.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10.5px] text-subtle">Composites</span>
          {composites.map(([color, url]) => {
            const p = gcsToPath(url);
            return p ? <Thumbnail key={color} path={p} alt={`text ${color}`} className="h-9 w-9" /> : null;
          })}
        </div>
      )}

      {editable && (
        <div className="mt-2.5 flex flex-wrap gap-1.5 border-t border-divider pt-2.5">
          <input
            ref={fileRef} type="file" accept="image/*,.svg,image/svg+xml" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void replaceFile(f); e.target.value = ''; }}
          />
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => fileRef.current?.click()}>
            <Upload className="h-3.5 w-3.5" aria-hidden /> {busy === 'replace' ? 'Uploading…' : 'Replace'}
          </Button>
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setTextOpen((v) => !v)}>
            <TypeIcon className="h-3.5 w-3.5" aria-hidden /> Text
          </Button>
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={removeLogo} className="text-danger hover:bg-danger/[0.06]">
            <Trash2 className="h-3.5 w-3.5" aria-hidden /> Remove
          </Button>
        </div>
      )}

      {textOpen && (
        <div className="mt-2 space-y-2 rounded-[10px] bg-bg/60 p-2.5">
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={100} placeholder="Text under logo (blank = remove)" />
          <div className="flex gap-2">
            <select className="h-7 flex-1 rounded-[8px] border border-black/[0.08] bg-surface px-2 text-[11.5px]" value={font} onChange={(e) => setFont(e.target.value)}>
              <option value="default">default</option>
              {fonts.filter((f) => f !== 'default').map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <Button size="sm" disabled={!!busy} onClick={applyText}>
              {busy === 'text' ? 'Applying…' : 'Apply'}
            </Button>
          </div>
          <p className="text-[10px] text-muted">Generates white + black text composites; preflight per text color follows.</p>
        </div>
      )}

      <div className="mt-2.5">
        {preflightEntries.length === 0 ? (
          <p className="text-[11px] text-muted">No preflight runs yet.</p>
        ) : (
          preflightEntries
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([cellKey, entry]) => (
              <PreflightCellRow key={cellKey} storeId={store.id} logoId={logoId} cellKey={cellKey} entry={entry} />
            ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: () => void;
  storeId: string | null;
};

export function StoreEditorDrawer({ open, onClose, storeId }: Props) {
  const key = open && storeId ? `/api/active-stores/${storeId}` : null;
  const { data: store, isLoading, mutate: refreshSelf } = useSWR<TeamStoreDetail>(key, get);
  const refresh = async () => { await refreshSelf(); };

  const addRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [extendOpen, setExtendOpen] = useState(false);
  const [newEnd, setNewEnd] = useState('');

  const editable = !!store && EDITABLE.has(store.status || '');
  const approvable = !!store && APPROVABLE.has(store.status || '');

  async function listMutate() {
    // Refresh every list this drawer may sit on top of.
    await Promise.all([
      refresh(),
      mutate((k) => typeof k === 'string' && k.startsWith('/api/active-stores?'), undefined, { revalidate: true }),
      mutate((k) => typeof k === 'string' && k.startsWith('/api/pending-approvals'), undefined, { revalidate: true }),
    ]);
  }

  async function addLogo(file: File) {
    if (!store) return;
    setBusy('add');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      fd.append('label', `Logo ${(store.logos?.length || 0) + 1}`);
      const res = await fetch(`/api/team-stores/${store.id}/logos`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, body);
      toast.success('Logo added');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Upload failed'));
    } finally {
      setBusy(null);
    }
  }

  async function runFullPreflight() {
    if (!store) return;
    setBusy('preflight');
    try {
      const r = await post<{ allPassed?: boolean; status?: string }>(`/api/team-stores/${store.id}/run-preflight`, {});
      toast.success(r.allPassed ? 'Preflight passed — ready for approval' : 'Preflight finished with issues');
      await listMutate();
    } catch (e) {
      toast.error(errMsg(e, 'Preflight failed'));
    } finally {
      setBusy(null);
    }
  }

  async function overridePreflight() {
    if (!store) return;
    const reason = prompt('Override reason (recorded on the store):');
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('A reason is required to override preflight');
      return;
    }
    setBusy('override');
    try {
      await post(`/api/team-stores/${store.id}/override-preflight`, { reason: reason.trim() });
      toast.success('Override applied — store is ready for approval');
      await listMutate();
    } catch (e) {
      toast.error(errMsg(e, 'Override failed'));
    } finally {
      setBusy(null);
    }
  }

  async function approve() {
    if (!store) return;
    if (!confirm(`Approve ${store.schoolName || store.fundraiserName || store.id} and create Shopify products?`)) return;
    setBusy('approve');
    try {
      const r = await post<{ productsCreated?: number; collectionUrl?: string | null }>(
        `/api/team-stores/${store.id}/approve`, {}
      );
      toast.success(`Approved — ${r.productsCreated ?? 0} products created`);
      await listMutate();
    } catch (e) {
      toast.error(errMsg(e, 'Approve failed'));
    } finally {
      setBusy(null);
    }
  }

  async function extend() {
    if (!store || !newEnd) return;
    setBusy('extend');
    try {
      const r = await post<{ shopifySynced?: boolean; message?: string }>(
        `/api/team-stores/${store.id}/extend`, { newEndDate: newEnd }
      );
      if (r.shopifySynced) toast.success(r.message || 'Extended and synced to Shopify');
      else toast.warning(r.message || 'Extended in database; Shopify sync failed');
      setExtendOpen(false);
      setNewEnd('');
      await listMutate();
    } catch (e) {
      toast.error(errMsg(e, 'Extend failed'));
    } finally {
      setBusy(null);
    }
  }

  const logos = store?.logos ?? [];
  const tone = STATUS_TONE[store?.status || ''] || 'bg-bg text-muted';
  const shopUrl = store?.collectionUrl || store?.storeUrl;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[520px] overflow-y-auto sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle>{store?.schoolName || store?.organizationName || store?.fundraiserName || 'Team Store'}</SheetTitle>
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            {store?.storeNumberDisplay && <span className="font-mono text-[11px] text-muted">{store.storeNumberDisplay}</span>}
            {store?.status && <Badge className={tone}>{store.status}</Badge>}
            {store?.sportType && <span className="text-[11px] text-muted">{store.sportType}</span>}
            {store?.primaryColor && <span title={store.primaryColorName} className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: store.primaryColor }} />}
            {store?.secondaryColor && <span title={store.secondaryColorName} className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: store.secondaryColor }} />}
          </div>
        </SheetHeader>

        {isLoading || !store ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {/* Dates + extend */}
            <div className="rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[12px]">
                  <span className="text-muted">Runs</span>{' '}
                  {store.startDate ? new Date(store.startDate).toLocaleDateString() : '—'}
                  {' → '}
                  {store.endDate ? new Date(store.endDate).toLocaleDateString() : 'Open'}
                </div>
                <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => setExtendOpen((v) => !v)}>
                  <CalendarPlus className="h-3.5 w-3.5" aria-hidden /> Extend
                </Button>
              </div>
              {extendOpen && (
                <div className="mt-2 flex gap-2">
                  <Input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className="flex-1" />
                  <Button size="sm" disabled={!newEnd || !!busy} onClick={extend}>
                    {busy === 'extend' ? 'Extending…' : 'Apply'}
                  </Button>
                </div>
              )}
              {(store.extensions || []).length > 0 && (
                <p className="mt-2 text-[10.5px] text-subtle">
                  Extended {store.extensions!.length}× · last by {store.extensions!.at(-1)?.extendedBy || '—'}
                  {store.extensions!.at(-1)?.shopifySynced === false ? ' (Shopify sync failed)' : ''}
                </p>
              )}
              {shopUrl && (
                <a href={shopUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-[11.5px] text-primary underline-offset-2 hover:underline">
                  Open Shopify collection →
                </a>
              )}
            </div>

            <StoreNameNumberFontCard store={store} refresh={refresh} />

            {/* Logos */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
                  Logos {logos.length > 0 && `· ${logos.length}`}
                </h3>
                {editable && (
                  <>
                    <input
                      ref={addRef} type="file" accept="image/*,.svg,image/svg+xml" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) void addLogo(f); e.target.value = ''; }}
                    />
                    <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => addRef.current?.click()}>
                      <Plus className="h-3.5 w-3.5" aria-hidden /> {busy === 'add' ? 'Uploading…' : 'Add logo'}
                    </Button>
                  </>
                )}
              </div>
              {logos.length === 0 ? (
                <p className="rounded-[10px] border border-dashed border-border p-4 text-center text-[11.5px] text-muted">
                  {store.logoPath ? 'Legacy single-logo store — use Run Full Preflight below.' : 'No logos on this store yet.'}
                </p>
              ) : (
                <div className="space-y-3">
                  {logos.map((logo, idx) => (
                    <LogoCard key={logo.id ?? logo.logoId ?? idx} store={store} logo={logo} refresh={refresh} />
                  ))}
                </div>
              )}
            </div>

            {/* Pipeline actions */}
            {(approvable || editable) && (
              <div className="space-y-2 rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Pipeline</h3>
                {store.preflightOverridden && (
                  <p className="rounded-[8px] bg-warning/10 px-2.5 py-1.5 text-[11px] text-warning">
                    Preflight was overridden{store.preflightOverrideReason ? `: ${store.preflightOverrideReason}` : ''}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {store.logoPath && (
                    <Button size="sm" variant="secondary" disabled={!!busy} onClick={runFullPreflight}>
                      <Play className="h-3.5 w-3.5" aria-hidden /> {busy === 'preflight' ? 'Running…' : 'Run full preflight'}
                    </Button>
                  )}
                  {/* The server refuses override outside the pre-build statuses
                      (409 status_not_overridable); APPROVABLE is a subset of them. */}
                  {approvable && (
                    <Button size="sm" variant="secondary" disabled={!!busy} onClick={overridePreflight}>
                      <ShieldAlert className="h-3.5 w-3.5" aria-hidden /> Override preflight
                    </Button>
                  )}
                  <Button size="sm" disabled={!!busy} onClick={approve}>
                    <CircleCheck className="h-3.5 w-3.5" aria-hidden /> {busy === 'approve' ? 'Working…' : 'Approve & create products'}
                  </Button>
                </div>
                <p className="text-[10.5px] text-subtle">
                  Approve requires every cell passing (or an override). Product creation runs in teamstore-createproducts.
                </p>
              </div>
            )}

            {store.status === 'creating_products' && (
              <div className="flex items-center gap-2 rounded-[12px] bg-primary/[0.06] p-3 text-[12px] text-primary">
                <RefreshCw className="h-4 w-4 animate-spin" aria-hidden /> Shopify products are being created…
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
